/* eslint-disable import/no-cycle */
import { NotificationTypes } from './notificationTypes';
import { LocalizedString } from './localizedString';
import { AppointmentStatus } from './appointmentStatus';
import { AppointmentType, OfferConditionsType } from './candidateAudit';
import { HireFormTypeEnum } from '../../../modules/messaging/seeker/header/actionsDropdown/HireFormTypeEnum';
import { ModernHireStatusList } from './modernHireTypes';
import { PositionType } from './position';
import { CorrectAnswerDeterminationType } from '../../../modules/administration/positionManagement/PositionType';
import { CandidatePositionNextStatus } from '../../../modules/messaging/seeker/header/candidatesDropdown/candidatePositionNextStatus/candidatePositionNextStatusConfig';

export enum FitStatus {
  GOOD = 'GOOD',
  MEDIUM = 'MEDIUM',
  BAD = 'BAD',
}
export type CommuteData = {
  durationInSeconds: number;
  distanceInMiles: number;
  calculationsCount: number;
  travelMode: string;
};

export type CandidateResponseT = {
  data: Candidate[];
};
export type Candidate = {
  account: string;
  email: string;
  allKnockOutQuestionsAnswered?: boolean;
  accountNotification?: AccountNotification;
  appointment?: Appointment;
  assignedRecruiters?: string[];
  watchedRecruiters?: string[];
  businessName: string;
  countryCode?: string;
  computedStatus?: {
    category?: string;
    lastUpdate: Date;
    status: CandidateStatus;
  };
  hireDetailsStatus?: string;
  offerAcceptanceStatus?: {
    offerAccepted: boolean;
    reason?: string;
  };
  offerStatus?: string;
  hired?: boolean;
  knockedOut?: boolean;
  knockedOutAt?: Date;
  seekerLookingForWork?: boolean;
  contactStatus?: ContactStatus;
  system?: SystemStatus;
  dismissed: boolean;
  dismissedReason?: string;
  rejectedReason?: string;
  accountExcluded?: boolean;
  displayProfilePicture?: boolean;
  firstName: string;
  id: string;
  jobTitle: LocalizedString;
  lastActivity: Date;
  lastName: string;
  monetized: boolean;
  position: string;
  positionOpen: boolean;
  questionResponses: QuestionResponse[];
  freeFormQuestionResponses?: FreeFormQuestionResponse[];
  multipleChoiceQuestionResponses?: MultipleChoiceQuestionResponse[];
  videoUploadQuestionResponses?: VideoUploadQuestionResponse[];
  documentUploadQuestionResponses?: DocumentUploadQuestionResponse[];
  seeker: string;
  insights?: {
    flexibleTransportation?: boolean;
    highSalaryDemand?: boolean;
  };
  preferredSalary?: {
    unit: string;
    value: number;
  };
  seekerNotification?: SeekerNotification;
  screeningDetails?: Screening;
  automation?: CandidateAutomation;
  recruiterInterviewFeedback?: {
    isAnonymousUser: boolean;
    lastEdited: Date;
    rating: string;
    recruiterId: string;
    review: string;
  }[];
  recruiterInterviewReview?: {
    [assignedRecruiters: string]: {
      lastEdited?: Date;
      rating: string;
      review: string;
      assessmentReason: string;
      assessment?: string;
      interviewType: AppointmentType;
      date: Date;
      businessId?: string;
      recruiterName?: string;
      isAnonymousUser?: boolean;
      email?: string;
    };
  };
  recruiterInterviewReviews?: {
    lastEdited?: Date;
    rating: string;
    review: string;
    assessmentReason: string;
    assessment?: string;
    interviewType: AppointmentType;
    date: Date;
    businessId?: string;
    recruiterName?: string;
    isAnonymousUser?: boolean;
    email?: string;
    recruiterId?: string;
  }[];
  ghostingStatus?: {
    status: GhostingStatus;
    lastUpdatedDate: Date;
  };
  lastMessageAt?: { fromRecruiter?: Date; fromSeeker?: Date };
  deactivated?: boolean;
  scheduledDeletion?: boolean;
  experience?: {
    maximumExperience: number;
    minimumExperience: number;
    totalExperience: number;
  };
  availability?: {
    day: boolean;
    night: boolean;
    evening: boolean;
  };
  blacklisted?: boolean;
  availabilityUpdatedAt?: Date;
  modernHire?: {
    status: ModernHireStatusList;
    startedDate?: Date;
    completedData?: Date;
    score?: number;
  };
  hireDetailsResubmissionRequest?: ResubmissionRequestFields[];
  requisitionId?: string;
  newHireData?: {
    positionId?: string;
    jobTitle?: LocalizedString;
    businessId?: string;
    businessName?: string;
  };
  newAvailabilities?: { [key: string]: { start_1: string; end_1: string } };
  nightAvailabilities?: { [key: string]: { start_1: string; end_1: string } };
  availabilityMatchedRequisitions?: MatchedRequisition[];
  availabilityMatchedPositions?: MatchedRequisition[];
  availabilityFitBand?: number;
  forSorting?: Date;
  earliestAvailabilityDate?: Date;
  recording?: RecordingType;
  languageEvaluationReview?: LanguageEvaluationReview;
  recordingRequested?: boolean;
  offerMadeAt?: Date;
  hiredAt?: Date;
  hiredBy?: string;
  hiredByFullName?: string;
  offerMadeByFullName?: string;
  hiredByFormApproval?: boolean;
  anonymousRecruiterInterviewReview?: AnonymousRecruiterInterviewReview[];
  priority?: boolean;
  kioskEnabled?: boolean;
  interventionEnabled?: boolean;
  businessCompleteAddress?: string;
  bookmarked?: boolean;
  modernHireEnable: boolean;
  commuteData?: CommuteData;
  recordingStatus?: RecordingStatus;
  resume?: Resume;
  positionalModernHireDisabled?: boolean;
  offboardingDetails?: {
    offboardedTime: Date;
    reason: OffboardingReason;
    type: string;
    customReason?: string;
  };
  hireFormTemplateId?: string | null;
  workbright?: {
    id?: number;
    lastUpdated?: Date;
    status: I9StatusList;
  };
  business?: string;
  positionId?: string;
  seekerId: string;
  joinStatus?: {
    dateOfJoining: Date;
  };
  isSeekerPreviouslyMonetized?: boolean;
  hiringSource?: {
    metaDataId: string;
    type: string;
  };
  detailStatus?: {
    current: string;
    next: CandidatePositionNextStatus;
    updatedAt: Date;
  };
};

export enum I9StatusList {
  REQUESTED = 'REQUESTED',
  SUBMISSION_ACCEPTED = 'SUBMISSION_ACCEPTED',
  IN_PROGRESS = 'IN_PROGRESS',
  ALL_FORMS_RECEIVED = 'ALL_FORMS_RECEIVED',
  SUBMISSION_REJECTED = 'SUBMISSION_REJECTED',
  FORM_SUBMITTED = 'FORM_SUBMITTED',
  NEW_FORM_SUBMISSION_REQUESTED = 'NEW_FORM_SUBMISSION_REQUESTED',
  ALL_REQUIRED_SUBMISSION_FINALIZED = 'ALL_REQUIRED_SUBMISSION_FINALIZED',
}

export enum OffboardingReason {
  END_OF_CONTRACT = 'END_OF_CONTRACT',
  OTHER = 'OTHERS',
}

export interface Resume {
  fileName: string;
  mimeType?: string;
  updatedAt?: Date;
  createdAt?: Date;
}

export enum RecordingStatus {
  REQUESTED = 'REQUESTED',
  RE_REQUESTED = 'RE_REQUESTED',
  IN_REVIEW = 'IN_REVIEW',
  REVIEW_COMPLETED = 'REVIEW_COMPLETED',
}
export type AnonymousRecruiterInterviewReview = {
  rating?: number;
  review?: string;
  date?: Date;
  positionId?: string;
  businessId?: string;
  interviewType?: AppointmentType;
  collaboratorName?: string;
  assessment?: string;
  assessmentReason?: string;
};

export type RecordingType = {
  createdAt: Date;
  language: string;
  metadata: {
    audioSource: number;
    bitsPerSample: number;
    channels: number;
    mimeType: string;
    sampleRate: number;
  };
  phraseId: string;
  phrase: string;
  url: string;
};

export type MatchedRequisition = {
  requisitionId: string;
  totalPointsPerRequisition: number;
  fit?: FitStatus;
};

export type ResubmissionRequestFields = {
  comment: string;
  field: string;
  label?: Record<string, string>;
};

export enum GhostingStatus {
  NOTIFICATION_SENT = 'NOTIFICATION_SENT',
  GHOSTED = 'GHOSTED',
  UNGHOSTED = 'UNGHOSTED',
}

export type Screening = {
  applicantId: string;
  reportStatus: string;
  screeningStatus: string;
  status: string;
  lastUpdatedDate?: Date;
  result?: string;
};

export type SeekerIsTyping = {
  lastUpdate: Date | undefined;
};

export type EngagedCandidate = Candidate & {
  monetized: true;
  email: string;
  phone: string;
  shouldShowInFilter?: boolean;
};

export type EngagedCandidateNotification = {
  candidates: EngagedCandidate[];
  lastId: string;
};

export type Appointment = PositionBasedAppointment | RecruiterBasedAppointment;

export type PositionBasedAppointment = BaseAppointment & {
  period?: string;
  appointmentAvailabilityId?: string;
  bookingId?: string;
  recruiterName?: string;
};

export type RecruiterBasedAppointment = BaseAppointment & {
  bookingId?: string;
  appointmentAvailabilityId?: string;
  positionId?: string;
  joinedRecruiterId?: string;
  recruiterName?: string;
};

type BaseAppointment = {
  adhoc?: boolean;
  status?: AppointmentStatus;
  toDate?: Date;
  fromDate?: Date;
  candidateId?: string;
  type?: AppointmentType;
  inPerson?: boolean;
  collaborators?: {
    email: string;
  }[];
};

export type AccountNotification = {
  lastSeekerMessageAt?: Date;
  lastUpdatedAt: Date;
  subscribers: string[];
  status: { [key: string]: { read: boolean; lastSeenAt: Date; unreadMessages: number } };
  lastNotificationType?: NotificationTypes;
};

export type SeekerNotification = {
  lastEmployerMessageAt?: Date;
  lastNotificationType?: string;
  lastSeenAt?: Date;
  lastUpdatedAt?: Date;
  read?: boolean;
  unreadMessages?: number;
};

export enum CandidateCategory {
  Other = 'OTHER',
}

export enum CandidateStatus {
  Dismissed = 'DISMISSED',
  NotLookingForWork = 'NOT_LOOKING_FOR_WORK',
  Hired = 'HIRED',
  HiredOnAnotherPosition = 'HIRED_ON_ANOTHER_POSITION',
  ContactRequested = 'CONTACT_REQUESTED',
  ContactRequestAccepted = 'CONTACT_REQUEST_ACCEPTED',
  ContactRequestDeclined = 'CONTACT_REQUEST_DECLINED',
  AppointmentRequested = 'APPOINTMENT_REQUESTED',
  AppointmentAccepted = 'APPOINTMENT_ACCEPTED',
  AppointmentDeclined = 'APPOINTMENT_DECLINED',
  AppointmentCancelled = 'APPOINTMENT_CANCELLED',
  AppointmentReschedulingNeeded = 'APPOINTMENT_RESCHEDULING_NEEDED',
  Bookmarked = 'BOOKMARKED',
  OfferMade = 'OFFER_MADE',
  OfferRejected = 'OFFER_REJECTED',
  Unhired = 'UNHIRED',
  Liked = 'LIKED',
  Unknown = 'UNKNOWN',
  InterestRevoked = 'INTEREST_REVOKED',
  Blacklisted = 'BLACKLISTED',
  HireInReview = 'HIRE_IN_REVIEW',
  HireFormCompleted = 'HIRE_FORM_COMPLETED',
  Reapplied = 'CANDIDATE_RE_APPLIED',
}
type QuestionReviewedBy = {
  recruiterId: string;
  recruiterName: string;
  reviewedAt: string;
};

export type QuestionReviewResponse = {
  isAnswerAccepted?: boolean;
  reviewedBy?: QuestionReviewedBy;
};

export type QuestionResponse = {
  id: string;
  answer: boolean;
  preferredAnswer: boolean;
  knockout: boolean;
  isActive?: boolean;
  text?: string;
  isPausable?: boolean;
} & QuestionReviewResponse;

export type FreeFormQuestionResponse = {
  id: string;
  answer?: string;
  isActive?: boolean;
  isPausable?: boolean;
  text?: string;
} & QuestionReviewResponse;

export type MultipleChoiceQuestionResponse = {
  id: string;
  answers: {
    id: string;
    answer: string;
    preferredAnswer?: boolean;
  }[];
  images?: [
    {
      url: string;
      path: string;
      galleryId: string;
    },
  ];
  isAnsweredCorrectly: boolean;
  knockout: boolean;
  question: string;
  preferredAnswers?: string[];
  text?: string;
  isPausable?: boolean;
  options: {
    id: string;
    text: string;
    wasAnswered: boolean;
  }[];
  totalAnswer?: number | 'any';
  correctAnswerDeterminationType?: CorrectAnswerDeterminationType;
  shouldMatchAtLeast?: number;
} & QuestionReviewResponse;

export interface ReviewedBy {
  recruiterId: string;
  recruiterName: string;
}
export type VideoUploadQuestionResponse = {
  answer: string | null;
  answer_hls: string | null;
  id: string;
  isActive: boolean;
  isAnswerAccepted?: boolean;
  isPausable: boolean;
  isSkippable: boolean;
  maximumVideoDuration: number;
  question: string;
  questionOrder: number;
  reviewed: boolean;
  shouldReaskOnReapply: boolean;
  skipped: boolean;
  reviewedBy?: ReviewedBy;
  reviewedAt?: string | Date;
  skippedAt?: string | Date;
};

export type VideoUploadQuestionResponses = VideoUploadQuestionResponse[];

export type DocumentUploadQuestionResponse = {
  id: string;
  reviewed: boolean;
  isActive?: boolean;
  isPausable?: boolean;
  isSkippable?: boolean;
  answers?: string[];
  maximumDocumentCount?: number;
  question?: string;
  questionOrder?: number;
  shouldReaskOnReapply?: boolean;
  skipped?: boolean;
};

export type CandidateDocumentUploadData = {
  documentUploadQuestionResponses: DocumentUploadQuestionResponse[];
  email: string;
};

export type DocumentUploadQuestionResponses = DocumentUploadQuestionResponse[];

export enum SeekerNoteType {
  NOTE = 'NOTE',
  FEEDBACK = 'FEEDBACK',
  OFFER_DETAILS = 'OFFER_DETAILS',
  OFFER_REJECTED_DETAILS = 'OFFER_REJECTED_DETAILS',
  HIRE_DETAILS = 'HIRE_DETAILS',
  UNHIRE_DETAILS = 'UNHIRE_DETAILS',
}

export type SeekerNote = {
  id: string;
  lastEdited?: Date;
  note?: string;
  notes?: SeekerNoteV2Item[];
  position: string;
  type?: SeekerNoteType | undefined;
  recruiter: string;
  recruiterName: string;
  seeker: string;
  isAnonymousUser?: boolean;
};

export type SeekerNoteV2Item = {
  note: string;
  lastEdited: Date;
  type: SeekerNoteType | undefined;
  isRemoved?: boolean | undefined;
  additionalContextData?: {
    salary: number;
    salaryUnit?: string;
    customSalaryUnit?: string;
    dateOfJoining?: Date;
  };
  originalIndex: number;
  recruiterRating?: string;
  isCollaboratorNote?: boolean;
  isAnonymousUser?: boolean;
  offerConditions?: OfferConditionsType | undefined;
};

export type CollaboratorNotes = {
  businessName: string;
  customName: string;
  notes: SeekerNote[];
  recruiterInterviewReview: {
    [key: string]: {
      assesment: string;
      assessmentReason: string;
      businessId: string;
      date: Date;
      interviewType: SeekerNoteType | undefined;
      lastEdited: Date;
      rating: number;
      review: string;
    };
  };
};

export type BackgroundChecks = {
  request_instant_verify_education: boolean;
  request_instant_verify_employment: boolean;
  request_enhanced_identity_verification: boolean;
  request_criminal_record_check: boolean;
  request_enhanced_criminal_record_check: boolean;
  request_us_criminal_record_check_tier_1: boolean;
  request_us_criminal_record_check_tier_2: boolean;
  request_soquij: boolean;
  request_right_to_work: boolean;
};

export type SystemStatus = {
  shouldCreateAppointmentRequest: boolean;
  shouldBeDismissed: boolean;
};

export enum ContactRequestStatus {
  requested = 'requested',
  accepted = 'accepted',
  declined = 'declined',
  revoked = 'revoked',
  reapplied = 'reapplied',
}

export enum AudioRatingType {
  fluency = 'fluency',
  pronunciation = 'pronunciation',
  accuracy = 'accuracy',
}

export enum LanguageEvaluationRating {
  EXPERT = 'EXPERT',
  COMPETENT = 'COMPETENT',
  LIMITED = 'LIMITED',
  OCCASIONAL = 'OCCASIONAL',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
}

export enum LanguageEvaluationStatus {
  PASS = 'PASS',
  FAIL = 'FAIL',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
}

export type LanguageEvaluationReview = {
  language: string; // en, fr
  phraseId: string;
  phrase: string;
  rating: LanguageEvaluationRating;
  fluency: number;
  pronunciation: number;
  accuracy: number;
  status: LanguageEvaluationStatus;
  automated?: boolean;
};

export type ContactStatus = {
  status: ContactRequestStatus;
  text?: string;
  declinedAt?: Date | null;
  sentAt?: Date | null;
  requestcount: number;
  toDate?: Date | null;
};

export type CandidateAutomation = {
  automatedContactRequest: boolean;
  automatedMonetization: boolean;
  automatedBookmark: boolean;
  automatedAppointmentRequest: boolean;
};

export type SeekerHireDetail = {
  type: HireFormTypeEnum;
  id: string;
  address: {
    addressLineOne?: string;
    addressLineTwo: string;
    city?: string;
    country?: string;
    postalCode: string;
    region?: string;
  };
  positionId: string;
  createdAt: Date;
  gender: string;
  name: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  nickname: string;
  email: string;
  correspondenceLanguage: string;
  optionalPhoneNumber: string;
  postalCode: string;
  positionName: null | string;
  seekerId: string;
  dateOfBirth?: Date;
  socialInsuranceNumber: string;
  emergencyContact: { firstName: string; lastName: string; phoneNumber: string };
  apartment?: string;
  startingDate: Date;
  wage: { value: number; unit: string; customUnit?: string };
  phoneNumber: string;
  fullTime: boolean;
  chequebookImageUrl: string | string[];
  updatedAt: Date;
  businessName: string;
  business: {
    address: string;
    name: string;
    country?: string;
  };
  accountId: string;
  signatureImageUrl: string | string[];
  note?: string;
  endDate?: Date;
  partTimeHours: string;
  miscellaneous?: {
    contractDetailsWordings: string;
  };
  workPermitEndDate?: Date;
  workPermitImageUrl?: string | string[];
  documentIdImageUrl?: string | string[];
  canadianBankAccountDetails?: CanadianBankAccountDetails;
  americanBankAccountDetails?: AmericanBankAccountDetails;
  positionType?: PositionType;
};

export type CandidateHireFormDetailResponse = {
  seekerHireDetail: SeekerHireDetail;
  positionName: null | string;
  businessName: string;
};

export type CandidateHireFormDetailResponseWithError = {
  code: string;
  status: number;
  title: string;
  traceId: string;
};

export type CanadianBankAccountDetails = {
  branchTransitNumber: string;
  financialInstitutionNumber: string;
  bankAccountNumber: string;
};

export type AmericanBankAccountDetails = {
  routingNumber: string;
  bankAccountNumber: string;
};

export type CandidateHireDetailsFollowUpAnswer = {
  field: string;
  questionId: string;
  inputType: string;
  section: string;
  label: {
    en: string;
    fr: string;
  };
  order: any;
  isFollowUp: boolean;
  answer: string;
  parent: string;
  optionId: string;
  isInfo: boolean;
  questionText: {
    en: string;
    fr: string;
  };
  messageType?: string;
};

export type CandidateHireDetailsAnswer = {
  field: string;
  questionId: string;
  inputType: string;
  section: string;
  sectionTitle: {
    en: string;
    fr: string;
  } | null;
  label: {
    en: string;
    fr: string;
  };
  order: any;
  isFollowUp?: boolean;
  answer: any;
  parent?: string;
  optionId?: string;
  isInfo: boolean;
  questionText: {
    en: string;
    fr: string;
  };
  messageType?: string;
  followups?: Array<CandidateHireDetailsFollowUpAnswer>;
  followUpField?: string;
};

export type CandidateHireDetailsResponse = {
  data: {
    answers: {
      answers: Array<CandidateHireDetailsAnswer> | null;
      defaultLanguage?: string;
      languages?: string[];
    };
  };
};

export type InReviewQuestionListType = {
  questionResponses?: QuestionResponse[];
  freeFormQuestionResponses?: FreeFormQuestionResponse[];
  multipleChoiceQuestionResponses?: MultipleChoiceQuestionResponse[];
  videoUploadQuestionResponses?: VideoUploadQuestionResponse[];
  documentUploadQuestionResponses?: DocumentUploadQuestionResponse[];
};
export interface RecruiterInterviewReview {
  lastEdited?: Date;
  rating: string;
  review: string;
  assessmentReason: string;
  interviewType: AppointmentType;
  date: Date;
  businessId?: string;
  recruiterName?: string;
  isAnonymousUser?: boolean;
  email?: string;
}
